import { useNavigate } from 'react-router-dom';
import { Target, Plus, ChevronRight, Sparkles } from 'lucide-react';
import { Card, Button, Skeleton, cn } from '@techsofit/ui';
import { useAsync } from '../lib/useAsync';
import { getMemberGoals } from '../lib/gymApi';
import { GOAL_STATUS_META, valueLabel, cadenceLabel } from '../lib/goals';
import { formatDate } from '../lib/format';

/** The member's goals at a glance, with a progress bar each. Tap one to open it. */
export function GoalsCard({ memberId }: { memberId: string }) {
  const navigate = useNavigate();
  const { data, loading } = useAsync(() => getMemberGoals(memberId), [memberId]);
  const goals = data ?? [];

  return (
    <Card className="mt-3 overflow-hidden p-0">
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink">
          <Target className="size-4 text-muted" aria-hidden /> Goals
        </h2>
        <div className="flex items-center gap-2">
          {goals.some((g) => g.checkinCount > 0) && (
            <Button size="sm" variant="ghost" onClick={() => navigate(`/members/${memberId}/wrapped`)}>
              <Sparkles className="size-4" aria-hidden />
              <span className="hidden sm:inline">Glow Up</span>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/members/${memberId}/goals/new`)}>
            <Plus className="size-4" aria-hidden /> Add goal
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2 p-4">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : goals.length ? (
        <ul className="divide-y divide-line">
          {goals.map((g) => {
            const meta = GOAL_STATUS_META[g.status];
            return (
              <li key={g.id}>
                <button
                  onClick={() => navigate(`/goals/${g.id}`)}
                  className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-surface"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium text-ink">{g.title}</p>
                      <span className={cn('shrink-0 rounded-pill px-2 py-0.5 text-xs font-medium', meta.cls)}>{meta.label}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted tabular">
                      {valueLabel(g.currentValue, g.unit)}
                      {g.targetValue != null && ` → ${valueLabel(g.targetValue, g.unit)}`} · {cadenceLabel(g.cadenceDays)}
                    </p>
                    {g.progressPercent != null && (
                      <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-pill bg-surface">
                        <div className="h-full rounded-pill bg-accent" style={{ width: `${g.progressPercent}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {g.status === 'ACTIVE' && <span className="hidden text-xs text-muted sm:inline">Next {formatDate(g.nextCheckInOn)}</span>}
                    <ChevronRight className="size-4 text-muted" aria-hidden />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-5 py-8 text-center text-sm text-muted">No goals yet — add one to start tracking progress.</p>
      )}
    </Card>
  );
}
